import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  PiArrowRight,
  PiArrowsClockwise,
  PiCalendarBlank,
  PiCalendarCheck,
  PiCoins,
  PiTrendDown,
  PiTrendUp,
  PiWallet,
} from 'react-icons/pi';
import { useFinancialPrivacy } from '../../FinancialPrivacyContext';
import SensitiveMoney from '../SensitiveMoney';
import {
  TRANSACTION_STATUS,
  TRANSACTION_TYPES,
  addCivilDays,
  compareFinancialRecordsByDate,
  fromFirestoreStatus,
  fromFirestoreType,
  getAmountCents,
  getFinancialDate,
  getYearMonthKey,
  getTodayCivilDate,
} from '../../domain/finance';
import { compareYearMonthKeys, formatYearMonthLabel, getCurrentYearMonthKey, toYearMonthParam } from '../../utils/period';
import TransactionDateBadge from '../TransactionDateBadge';
import BudgetSummaryCard from '../BudgetSummaryCard';
import { getTransactionDisplayName } from '../../utils/transactionPresentation';
import './styles.css';

const MAX_UPCOMING = 4;
const MAX_RECENT = 6;
const MAX_PERIOD_TRANSACTIONS = 4;

export const getBalanceTrend = (currentCents, previousCents) => {
  if (!Number.isFinite(currentCents) || !Number.isFinite(previousCents) || previousCents === 0) return null;
  const delta = currentCents - previousCents;
  const percentage = Math.abs(delta / Math.abs(previousCents)) * 100;
  const value = percentage < 1 && delta !== 0
    ? 'Menos de 1%'
    : percentage > 999
      ? 'Mais de 999%'
      : `${Math.round(percentage)}%`;
  return { direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat', value };
};


const getPeriodEndLabel = (period) => {
  const parts = String(period).split('/').map(Number);
  if (!parts[0] || !parts[1]) return 'o fim do período';
  const lastDay = new Date(parts[1], parts[0], 0).getDate();
  const monthLabel = formatYearMonthLabel(period, { includeYear: false })?.toLocaleLowerCase('pt-BR');
  return monthLabel ? lastDay + ' de ' + monthLabel : 'o fim do período';
};

const getStatusLabel = (item) => (
  (fromFirestoreStatus(item.status) || item.status) === TRANSACTION_STATUS.PENDING
    ? 'Pendente'
    : (fromFirestoreType(item.type) || item.tipo) === TRANSACTION_TYPES.INCOME ? 'Recebido' : 'Pago'
);

const compareAgendaItems = (left, right) => {
  const leftPending = (fromFirestoreStatus(left.status) || left.status) === TRANSACTION_STATUS.PENDING;
  const rightPending = (fromFirestoreStatus(right.status) || right.status) === TRANSACTION_STATUS.PENDING;
  if (leftPending !== rightPending) return leftPending ? -1 : 1;
  const dateComparison = compareFinancialRecordsByDate(left, right) || 0;
  if (dateComparison !== 0) return dateComparison;
  const leftKey = `${left.source || 'transaction'}:${left.id}`;
  const rightKey = `${right.source || 'transaction'}:${right.id}`;
  return leftKey.localeCompare(rightKey);
};

const getRecordTime = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getItemKey = (item) => String(item.id || item.transactionId || item.occurrenceKey || '');

const uniqueItems = (items) => Array.from(new Map(
  items.map((item) => [`${item.source || 'transaction'}:${getItemKey(item)}`, item])
).values());

const compareRecentItems = (left, right) => {
  const dateComparison = compareFinancialRecordsByDate(left, right) || 0;
  if (dateComparison !== 0) return -dateComparison;
  const activityComparison = Math.max(getRecordTime(right.updatedAt), getRecordTime(right.createdAt))
    - Math.max(getRecordTime(left.updatedAt), getRecordTime(left.createdAt));
  if (activityComparison !== 0) return activityComparison;
  return getItemKey(left).localeCompare(getItemKey(right));
};

const getTransactionUrl = (item) => {
  const params = new URLSearchParams();
  const periodParam = toYearMonthParam(getYearMonthKey(getFinancialDate(item)));
  if (periodParam) params.set('period', periodParam);
  if (item.id) params.set('edit', item.id);
  const query = params.toString();
  return `/movimentacoes${query ? `?${query}` : ''}`;
};

const FinancialRow = ({ item, showValues, onOpenVirtualOccurrence }) => {
  const amountCents = getAmountCents(item) || 0;
  const itemType = fromFirestoreType(item.type) || item.tipo;
  const itemStatus = fromFirestoreStatus(item.status) || item.status;
  const income = itemType === TRANSACTION_TYPES.INCOME;
  const valueClass = 'nf-financial-value ' + (income ? 'is-income' : 'is-expense');
  const itemName = getTransactionDisplayName(item);
  const itemCategory = item.category || item.categoria || 'Sem categoria';
  const isVirtual = item.isVirtual === true;

  const content = (
    <>
      <TransactionDateBadge record={item} />
      <div className="home-v3-activity__copy">
        <strong>{itemName}</strong>
        <div className="home-v3-activity__meta">
          <span>{itemCategory}</span>
          {isVirtual && (
            <span className="home-v3-activity__recurring" aria-label="Recorrente" title="Recorrente">
              <PiArrowsClockwise aria-hidden="true" />
            </span>
          )}
        </div>
      </div>
      <div className="home-v3-activity__value">
        <SensitiveMoney
          as="strong"
          className={valueClass}
          valueCents={amountCents}
          semantic={income ? 'income' : 'expense'}
          showSign
          visible={showValues}
        />
        <span className={itemStatus === TRANSACTION_STATUS.PENDING ? 'is-pending' : ''}>
          {getStatusLabel(item)}
        </span>
      </div>
    </>
  );

  return (
    <li className="home-v3-activity__entry">
      {isVirtual ? (
        <button
          type="button"
          className="home-v3-activity__item is-virtual"
          onClick={() => onOpenVirtualOccurrence?.(item)}
          aria-label={`Ver previsão recorrente ${itemName}`}
        >
          {content}
        </button>
      ) : (
        <Link
          className="home-v3-activity__item"
          to={getTransactionUrl(item)}
          aria-label={`Editar movimentação ${itemName}`}
        >
          {content}
        </Link>
      )}
    </li>
  );
};

FinancialRow.propTypes = {
  item: PropTypes.object.isRequired,
  showValues: PropTypes.bool.isRequired,
  onOpenVirtualOccurrence: PropTypes.func,
};

const HomeDashboard = ({
  summary,
  previousSummary = null,
  transactions,
  upcomingTransactions = transactions,
  recentTransactions = transactions,
  upcomingLoading = false,
  upcomingError = null,
  onRetryUpcoming,
  recentLoading = false,
  recentError = null,
  onRetryRecent,
  onOpenVirtualOccurrence,
  showValues,
  selectedPeriod,
  showBudgetSummary = false,
  budget = null,
  budgetUsage = null,
  budgetLoading = false,
  budgetError = null,
}) => {
  const privacy = useFinancialPrivacy();
  const valuesVisible = typeof showValues === 'boolean' ? showValues : privacy.valuesVisible;
  const today = getTodayCivilDate();
  const futureLimit = addCivilDays(today, 30);
  const periodPosition = compareYearMonthKeys(selectedPeriod, getCurrentYearMonthKey());
  const periodName = formatYearMonthLabel(selectedPeriod, { includeYear: false }) || 'mês';
  const periodLabel = formatYearMonthLabel(selectedPeriod) || 'período selecionado';
  const isCurrentPeriod = periodPosition === 0;
  const mainLabel = periodPosition < 0 ? 'Saldo do período' : periodPosition > 0 ? 'Saldo previsto' : 'Saldo atual';
  const freeAfterCommitments = summary.realizedBalanceCents - summary.plannedExpenseCents;
  const displayedBalanceCents = periodPosition > 0
    ? summary.projectedBalanceCents
    : summary.realizedBalanceCents;
  const balanceTrend = getBalanceTrend(
    displayedBalanceCents,
    previousSummary?.realizedBalanceCents
  );

  const activity = useMemo(() => {
    const upcoming = uniqueItems(upcomingTransactions)
      .filter((item) => {
        const date = getFinancialDate(item);
        const status = fromFirestoreStatus(item.status) || item.status;
        return date && date >= today && date <= futureLimit && status === TRANSACTION_STATUS.PENDING;
      })
      .sort(compareAgendaItems)
      .slice(0, MAX_UPCOMING);
    const recent = uniqueItems(recentTransactions)
      .filter((item) => {
        const date = getFinancialDate(item);
        return date && date <= today;
      })
      .sort(compareRecentItems)
      .slice(0, MAX_RECENT);
    const recentDated = transactions.filter((item) => Boolean(getFinancialDate(item)));
    const periodTransactions = recentDated
      .sort((left, right) => -(compareFinancialRecordsByDate(left, right) || 0))
      .slice(0, MAX_PERIOD_TRANSACTIONS);
    return { upcoming, recent, periodTransactions, periodTransactionCount: recentDated.length };
  }, [futureLimit, recentTransactions, today, transactions, upcomingTransactions]);

  const renderRows = (items) => (
    <ul>
      {items.map((item) => (
        <FinancialRow
          key={(item.source || item.tipo || 'transaction') + '-' + item.id}
          item={item}
          showValues={valuesVisible}
          onOpenVirtualOccurrence={onOpenVirtualOccurrence}
        />
      ))}
    </ul>
  );

  return (
    <main className={`home-v3${isCurrentPeriod ? '' : ' home-v3--period'}`}>
      <section className="home-v3-balance" aria-labelledby="home-balance-title">
        <img className="home-v3-balance__brand-mark" src="/nara-symbol-color.svg" alt="" data-testid="home-balance-brand-mark" aria-hidden="true" />
        <div className="home-v3-balance__heading">
          <div className="home-v3-balance__main">
            <div className="home-v3-balance__label">
              <span className="home-v3-balance__label-icon" aria-hidden="true"><PiWallet /></span>
              <p id="home-balance-title">{mainLabel}</p>
            </div>
            <div className="home-v3-balance__value nf-financial-value">
              <SensitiveMoney as="strong" valueCents={displayedBalanceCents} visible={valuesVisible} />
            </div>
            {balanceTrend && valuesVisible && (
              <div
                className={`home-v3-balance__trend is-${balanceTrend.direction}`}
                aria-label={`${balanceTrend.value} ${balanceTrend.direction === 'up' ? 'acima' : balanceTrend.direction === 'down' ? 'abaixo' : 'sem alteração'} em relação ao mês anterior`}
              >
                {balanceTrend.direction === 'down' ? <PiTrendDown aria-hidden="true" /> : <PiTrendUp aria-hidden="true" />}
                <span><strong>{balanceTrend.value}</strong> em relação ao mês anterior</span>
              </div>
            )}
          </div>
        </div>
        <div className="home-v3-balance__context">
          <div className="home-v3-balance__context-item">
            <span className="home-v3-balance__context-label"><PiCoins aria-hidden="true" />Livre após compromissos</span>
            <SensitiveMoney as="strong" className="nf-financial-value" valueCents={freeAfterCommitments} visible={valuesVisible} />
          </div>
          <div className="home-v3-balance__context-item">
            <span className="home-v3-balance__context-label"><PiCalendarCheck aria-hidden="true" />Comprometido</span>
            <SensitiveMoney as="strong" className="nf-financial-value" valueCents={summary.plannedExpenseCents} visible={valuesVisible} />
          </div>
        </div>
      </section>

      <section className="home-v3-forecast" aria-labelledby="home-forecast-title">
        <span className="home-v3-card-icon" aria-hidden="true"><PiWallet /></span>
        <div>
          <p id="home-forecast-title">Previsão até {getPeriodEndLabel(selectedPeriod)}</p>
          <SensitiveMoney as="strong" className="nf-financial-value" valueCents={summary.projectedBalanceCents} visible={valuesVisible} />
          <span>Considerando receitas e despesas cadastradas</span>
        </div>
      </section>

      {showBudgetSummary && budgetUsage && (
        <BudgetSummaryCard
          budget={budget}
          usage={budgetUsage}
          period={selectedPeriod}
          loading={budgetLoading}
          error={budgetError}
        />
      )}

      <section className="home-v3-flow" aria-labelledby="home-flow-title">
        <div className="home-v3-section-heading">
          <div><span>Visão mensal</span><h2 id="home-flow-title">Fluxo de {periodName.toLocaleLowerCase('pt-BR')}</h2></div>
        </div>
        <div className="home-v3-flow__grid">
          <div className="is-income"><span className="home-v3-flow__icon" aria-hidden="true"><PiTrendUp /></span><span>Entradas</span><SensitiveMoney as="strong" className="nf-financial-value" valueCents={summary.realizedIncomeCents} semantic="income" visible={valuesVisible} /></div>
          <div className="is-expense"><span className="home-v3-flow__icon" aria-hidden="true"><PiTrendDown /></span><span>Saídas</span><SensitiveMoney as="strong" className="nf-financial-value" valueCents={summary.realizedExpenseCents} semantic="expense" visible={valuesVisible} /></div>
          <div className="is-balance"><span className="home-v3-flow__icon" aria-hidden="true"><PiWallet /></span><span>Saldo do mês</span><SensitiveMoney as="strong" className="nf-financial-value" valueCents={summary.realizedBalanceCents} visible={valuesVisible} /></div>
        </div>
        {isCurrentPeriod && (
          <Link
            className="home-v3-activity__more home-v3-flow__cta"
            to={`/movimentacoes?period=${toYearMonthParam(selectedPeriod)}`}
          >
            Ver movimentações do mês <PiArrowRight aria-hidden="true" />
          </Link>
        )}
      </section>

      {isCurrentPeriod ? (
        <>
          <section className="home-v3-activity home-v3-activity--upcoming" aria-labelledby="home-upcoming-title">
            <div className="home-v3-section-heading">
              <div><span>Agenda financeira</span><h2 id="home-upcoming-title">Próximos 30 dias</h2></div>
              <Link to="/movimentacoes?view=future">Ver todos <PiArrowRight aria-hidden="true" /></Link>
            </div>
            {upcomingLoading ? (
              <div className="home-v3-activity__state" role="status">Carregando agenda financeira...</div>
            ) : upcomingError ? (
              <div className="home-v3-activity__state is-error" role="alert">
                <span>Não foi possível carregar a agenda financeira.</span>
                <button type="button" onClick={onRetryUpcoming}>Tentar novamente</button>
              </div>
            ) : activity.upcoming.length > 0 ? renderRows(activity.upcoming) : (
              <div className="home-v3-activity__empty"><PiCalendarBlank aria-hidden="true" /><span>Nenhuma movimentação prevista para os próximos 30 dias.</span></div>
            )}
          </section>

          <section className="home-v3-activity home-v3-activity--recent" aria-labelledby="home-recent-title">
            <div className="home-v3-section-heading">
              <div><span>Últimos registros</span><h2 id="home-recent-title">Movimentações recentes</h2></div>
              <Link to="/movimentacoes?view=recent">Ver todas <PiArrowRight aria-hidden="true" /></Link>
            </div>
            {recentLoading ? (
              <div className="home-v3-activity__state" role="status">Carregando movimentações recentes...</div>
            ) : recentError ? (
              <div className="home-v3-activity__state is-error" role="alert">
                <span>Não foi possível carregar as movimentações recentes.</span>
                <button type="button" onClick={onRetryRecent}>Tentar novamente</button>
              </div>
            ) : activity.recent.length > 0 ? renderRows(activity.recent) : (
              <div className="home-v3-activity__empty"><PiCalendarBlank aria-hidden="true" /><span>Nenhuma movimentação recente encontrada.</span></div>
            )}
          </section>
        </>
      ) : (
        <section className="home-v3-activity home-v3-activity--period" aria-labelledby="home-period-transactions-title">
          <div className="home-v3-section-heading">
            <div><span>Visão do período</span><h2 id="home-period-transactions-title">Movimentações de {periodLabel.toLocaleLowerCase('pt-BR')}</h2></div>
          </div>
          {activity.periodTransactions.length > 0 ? renderRows(activity.periodTransactions) : (
            <div className="home-v3-activity__empty"><PiCalendarBlank aria-hidden="true" /><span>Nenhuma movimentação cadastrada neste período.</span></div>
          )}
          {activity.periodTransactionCount > MAX_PERIOD_TRANSACTIONS && (
            <Link className="home-v3-activity__more" to={`/movimentacoes?period=${toYearMonthParam(selectedPeriod)}`}>
              Ver todas as {activity.periodTransactionCount} movimentações <PiArrowRight aria-hidden="true" />
            </Link>
          )}
        </section>
      )}
    </main>
  );
};

HomeDashboard.propTypes = {
  summary: PropTypes.shape({
    realizedBalanceCents: PropTypes.number.isRequired,
    projectedBalanceCents: PropTypes.number.isRequired,
    realizedIncomeCents: PropTypes.number.isRequired,
    realizedExpenseCents: PropTypes.number.isRequired,
    plannedExpenseCents: PropTypes.number.isRequired,
  }).isRequired,
  previousSummary: PropTypes.shape({
    realizedBalanceCents: PropTypes.number.isRequired,
  }),
  transactions: PropTypes.array.isRequired,
  upcomingTransactions: PropTypes.array,
  recentTransactions: PropTypes.array,
  upcomingLoading: PropTypes.bool,
  upcomingError: PropTypes.object,
  onRetryUpcoming: PropTypes.func,
  recentLoading: PropTypes.bool,
  recentError: PropTypes.object,
  onRetryRecent: PropTypes.func,
  onOpenVirtualOccurrence: PropTypes.func,
  showValues: PropTypes.bool,
  selectedPeriod: PropTypes.string.isRequired,
  showBudgetSummary: PropTypes.bool,
  budget: PropTypes.object,
  budgetUsage: PropTypes.object,
  budgetLoading: PropTypes.bool,
  budgetError: PropTypes.object,
};

export default HomeDashboard;
